<template>
  <div>
    <form class="form-horizontal">
      <div class="row mb-3">
        <div class="col-sm-12 col-lg-4 col-md-12">
          <div class="form-group row">
            <label class="col-md-4 form-col-label control-label required">
              {{ $t('accounting.exchange_transaction.customer_name') }}
              <i
                class="fas fa-spinner spin"
                v-if="!customers.length && ready_fetch"
              ></i>
            </label>
            <div class="col-md-8">
              <ts-search-select
                v-model.number="model.customer_id"
                :options="customerOptions"
                :disabled="!customers.length && ready_fetch"
              ></ts-search-select>
              <div class="validate" v-if="errors.has('customer_id')">
                {{ errors.first('customer_id') }}
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-md-4 form-col-label control-label required">
              {{ $t('accounting.exchange_transaction.employee_name') }}
              <i
                class="fas fa-spinner spin"
                v-if="!employees.length && ready_fetch"
              ></i>
            </label>
            <div class="col-md-8">
              <ts-search-select
                v-model.number="model.employee_id"
                :options="employeeOptions"
                :disabled="!employees.length && ready_fetch"
              ></ts-search-select>
              <div class="validate" v-if="errors.has('employee_id')">
                {{ errors.first('employee_id') }}
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-md-4 form-col-label control-label">
              {{ $t('accounting.exchange_transaction.description') }}
            </label>
            <div class="col-md-8">
              <textarea
                class="form-control"
                rows="4"
                v-model="model.description"
              >
              </textarea>
              <div class="invalid-feedback" v-if="errors.has('description')">
                {{ errors.first('description') }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-12 col-lg-8 col-md-12">
          <div class="form-group row">
            <label class="col-md-3 form-col-label control-label required">
              {{ $t('accounting.exchange_transaction.from_currency') }}
              <i
                class="fas fa-spinner spin"
                v-if="!currencies.length && ready_fetch"
              ></i>
            </label>
            <div class="col-md-7">
              <div class="d-flex">
                <select
                  :disabled="!currencies.length && ready_fetch"
                  @change="fromCurrencyChange"
                  v-model.number="model.currency_id_from"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('currency_id_from') }"
                >
                  <option value="" disabled selected style="display: none;">
                    {{ $t('select') }}</option
                  >
                  <option
                    v-for="(currency, index) in from_currencies"
                    :key="index"
                    :value="currency.currency_id"
                  >
                    {{ currency.currency_name_en }} |
                    {{ currency.currency_code }}
                  </option>
                </select>
                <label class="col-md-3 form-col-label control-label required">
                  {{ $t('accounting.exchange_transaction.to_currency') }}
                  <i
                    class="fas fa-spinner spin"
                    v-if="!currencies.length && ready_fetch"
                  ></i>
                </label>
                <select
                  ref="currency_id_to"
                  @change="toCurrencyChange"
                  :disabled="!currencies.length && ready_fetch"
                  v-model.number="model.currency_id_to"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('currency_id_to') }"
                >
                  <option value="" disabled selected style="display: none;">
                    {{ $t('select') }}</option
                  >
                  <option
                    v-for="(currency, index) in to_currencies"
                    :key="index"
                    :value="currency.currency_id"
                  >
                    {{ currency.currency_name_en }} |
                    {{ currency.currency_code }}
                  </option>
                </select>
              </div>
              <div class="validate" v-if="errors.has('currency_id_from')">
                {{ errors.first('currency_id_from') }}
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-md-3 form-col-label control-label required">
              {{ $t('accounting.exchange_transaction.amount_in') }}
            </label>
            <div class="col-md-7">
              <div class="d-flex">
                <div class="input-group">
                  <input
                    v-model.number="model.amount_in"
                    type="number"
                    ref="amount_in"
                    min="0"
                    class="form-control"
                    @input="amountOut"
                    :readonly="fetch_rate"
                    :class="{ 'is-invalid': errors.has('amount_in') }"
                  />
                  <div v-if="from_currency_sign" class="input-group-addon">
                    {{ from_currency_sign }}
                  </div>
                </div>
                <label class="col-md-3 form-col-label control-label">
                  {{ $t('accounting.exchange_transaction.rate_amount') }}
                  <i class="fas fa-spinner spin" v-if="fetch_rate"></i>
                </label>
                <div class="input-group">
                  <input
                    :value="model.rate_amount"
                    disabled
                    class="form-control"
                  />
                  <div v-if="to_currency_sign" class="input-group-addon">
                    {{ to_currency_sign }}
                  </div>
                </div>
              </div>
              <div class="validate" v-if="errors.has('amount_in')">
                {{ errors.first('amount_in') }}
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-md-3 form-col-label control-label required">
              {{ $t('accounting.exchange_transaction.amount_taken') }}
            </label>
            <div class="col-md-7">
              <div class="d-flex">
                <div class="input-group">
                  <input
                    v-model.number="model.amount_taken"
                    class="form-control"
                    min="0"
                    @input="amountTaken"
                    type="number"
                    :class="{ 'is-invalid': errors.has('amount_taken') }"
                  />
                  <div v-if="to_currency_sign" class="input-group-addon">
                    {{ to_currency_sign }}
                  </div>
                </div>
                <label class="col-md-3 form-col-label control-label">
                  {{ $t('accounting.exchange_transaction.amount_out') }}
                </label>
                <div class="input-group">
                  <input
                    disabled
                    class="form-control"
                    :value="formatCurrency(model.amount_out)"
                  />
                  <div v-if="to_currency_sign" class="input-group-addon">
                    {{ to_currency_sign }}
                  </div>
                </div>
              </div>
              <div class="validate" v-if="errors.has('amount_taken')">
                {{ errors.first('amount_taken') }}
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-md-3 form-col-label control-label"> </label>
            <div class="col-md-7">
              <div class="d-flex">
                <div class="input-group">
                  <ts-checkbox v-model="model.all_taken" disabled>{{
                    $t('accounting.exchange_transaction.all_taken')
                  }}</ts-checkbox>
                  <div v-if="false" class="input-group-addon"></div>
                </div>
                <label class="col-md-3 form-col-label control-label">
                  {{ $t('accounting.exchange_transaction.amount_due') }}
                </label>
                <div class="input-group">
                  <input
                    :value="formatCurrency(model.amount_due)"
                    disabled
                    class="form-control"
                  />
                  <div v-if="to_currency_sign" class="input-group-addon">
                    {{ to_currency_sign }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div class="text-right">
        <ts-button
          color="primary"
          :disabled="saving"
          :waiting="saving"
          @click="update"
          v-if="editing"
        >
          {{ $t('addministration.division_feature.update') }}
        </ts-button>
        <ts-button
          class="ml-2"
          color="primary"
          :disabled="saving"
          :waiting="saving"
          @click="save"
          v-else
        >
          {{ $t('addministration.division_feature.save') }}
        </ts-button>
        <ts-button class="ml-2" @click="cancel()" v-if="editing">
          {{ $t('addministration.division_feature.cancel') }}
        </ts-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Errors } from 'form-backend-validation'
import moment from 'moment'

export default {
  name: 'exchange-transaction-create',
  data() {
    return {
      model: {
        customer_id: '',
        employee_id: '',
        currency_id_from: '',
        currency_id_to: '',
        rate_amount: 0,
        amount_in: '',
        amount_out: 0,
        amount_taken: '',
        amount_due: 0,
        description: '',
        all_taken: false
      },
      errors: new Errors(),
      ready_fetch: false,
      fetch_rate: false,
      saving: false,
      editing: false,
      from_currency_sign: '',
      to_currency_sign: ''
    }
  },
  computed: {
    ...mapState('exchangeTransaction', [
      'customers',
      'employees',
      'currencies',
      'edit_data'
    ]),
    customerOptions() {
      return this.customers.map(el => ({
        value: el.customer_id,
        text: el.customer_name
      }))
    },
    employeeOptions() {
      return this.employees.map(el => ({
        value: el.employee_id,
        text: el.name_en
      }))
    },
    from_currencies() {
      return this.currencies.filter(
        c => c.currency_id !== this.model.currency_id_to
      )
    },
    to_currencies() {
      return this.currencies.filter(
        c => c.currency_id !== this.model.currency_id_from
      )
    }
  },
  created() {
    this.fetchResourceBeforeCreate()
  },
  methods: {
    async fetchResourceBeforeCreate() {
      this.ready_fetch = true
      await this.$store
        .dispatch('exchangeTransaction/dataBeforeCreate')
        .then(response => {
          this.ready_fetch = false
          this.setEditData()
          if (
            _.isEmpty(this.edit_data) ||
            this.edit_data === null ||
            this.edit_data === undefined
          ) {
            this.model.customer_id = this.customers.filter(
              c => c.is_default == true
            )[0].customer_id
          }
        })
        .catch(error => {
          this.ready_fetch = false
          this.$toasted.error(error.message)
        })
    },
    async fromCurrencyChange() {
      await this.getRateAmount()
      this.from_currency_sign = this.currencies.filter(
        c => c.currency_id == this.model.currency_id_from
      )[0].currency_sign
      this.$refs.currency_id_to.focus()
    },
    async toCurrencyChange() {
      await this.getRateAmount()
      this.to_currency_sign = this.currencies.filter(
        c => c.currency_id == this.model.currency_id_to
      )[0].currency_sign
    },
    getRateAmount() {
      if (this.model.currency_id_from == '' || this.model.currency_id_to == '')
        return
      this.model.rate_amount = ''
      this.fetch_rate = true
      this.$store
        .dispatch('exchangeTransaction/getRateAmount', {
          currency_id_from: this.model.currency_id_from,
          currency_id_to: this.model.currency_id_to
        })
        .then(response => {
          this.model.rate_amount = response[0]
            ? parseFloat(response[0].fn_get_exchange_rate)
            : 0
          this.fetch_rate = false
          this.$refs.amount_in.focus()
        })
        .catch(error => {
          this.fetch_rate = false
          this.$toasted.error(error.message)
        })
    },
    amountTaken() {
      this.model.amount_due = ''
      this.model.amount_due = this.model.amount_out - this.model.amount_taken
    },
    amountOut() {
      this.isAllTaken()
      if (this.model.amount_in == '' || this.model.rate_amount == '') return
      if (
        _.isEmpty(this.edit_data) ||
        this.edit_data === null ||
        this.edit_data === undefined
      ) {
        this.model.amount_out = this.model.amount_taken = this.amount_out = this.amount_taken = this.rate_amount =
          ''
        this.model.amount_taken = _.ceil(
          this.model.amount_in * this.model.rate_amount,
          2
        )
        this.model.amount_out = _.ceil(
          this.model.amount_in * this.model.rate_amount,
          2
        )
      } else {
        this.model.amount_out = _.ceil(
          this.model.amount_in * this.model.rate_amount,
          2
        )
      }
    },
    isAllTaken() {
      if (this.model.amount_due === 0) {
        this.model.all_taken = true
      } else {
        this.model.all_taken = false
      }
    },
    formatCurrency(value) {
      let val = (value / 1).toFixed(2).replace(',', '.')
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    save() {
      this.errors = new Errors()
      this.saving = true
      this.$store
        .dispatch('exchangeTransaction/store', this.model)
        .then(response => {
          this.saving = false
          this.clearForm()
          this.$emit('fetch_data')
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.saving = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    setEditData() {
      if (
        _.isEmpty(this.edit_data) ||
        this.edit_data === null ||
        this.edit_data === undefined
      )
        return
      this.errors = new Errors()
      this.editing = true
      this.model = {
        exc_id: this.edit_data.exc_id,
        customer_id: this.edit_data.customer_id,
        employee_id: this.edit_data.employee_id,
        currency_id_from: this.edit_data.currency_id_from,
        currency_id_to: this.edit_data.currency_id_to,
        amount_in: this.edit_data.amount_in,
        amount_out: this.edit_data.amount_out,
        amount_taken: this.edit_data.amount_taken,
        amount_due: this.edit_data.amount_due,
        rate_amount: this.edit_data.rate_amount,
        description: this.edit_data.description,
        all_taken: this.edit_data.all_taken
      }
    },
    update() {
      this.errors = new Errors()
      this.saving = true
      this.$store
        .dispatch('exchangeTransaction/update', this.model)
        .then(response => {
          this.saving = false
          this.cancel()
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.editing = true
          this.saving = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    cancel() {
      this.editing = false
      this.clearForm()
      this.$store.commit('exchangeTransaction/SET_EDIT_DATA', {})
      this.$emit('fetch_data')
      this.$router.push({ name: 'exchange-transaction' })
    },
    clearForm() {
      this.model = {
        customer_id: this.customers.filter(c => c.is_default == true)[0]
          .customer_id,
        employee_id: '',
        currency_id_from: '',
        currency_id_to: '',
        rate_amount: 0,
        amount_in: '',
        amount_out: 0,
        amount_taken: '',
        amount_due: 0,
        description: '',
        all_taken: false
      }
      this.from_currency_sign = ''
      this.to_currency_sign = ''
    }
  },
  watch: {
    edit_data: {
      handler() {
        if (this.edit_data) {
          this.setEditData()
        }
      }
    },
    'model.rate_amount': function(value) {
      if (value) {
        this.amountOut()
      }
    },
    'model.amount_taken': function(value) {
      if (value) {
        this.amountTaken()
      }
    },
    'model.amount_due': function(value) {
      this.isAllTaken()
    }
  }
}
</script>
<style lang="css" scoped>
 .no-border {
	border-top: none;
	border-left: none;
	border-right: none;
	outline: none;
}
</style>
